import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { User, Building2, CheckCircle, Home, Eye, EyeOff, Mail } from 'lucide-react';
import api, { apiErrorText } from '../../utils/api';
import { BillingTerm, DEFAULT_BILLING_TERM, isBillingTerm } from '../../config/pricing';

const OnboardingWizard: React.FC = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    password_confirm: '',
    full_name: '',
    company_name: '',
    plan: '',
    description: '',
    industry: '',
    company_size: '',
    work_model: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [billingTerm, setBillingTerm] = useState<BillingTerm>(DEFAULT_BILLING_TERM);
  const [kvkkConsent, setKvkkConsent] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const planParam = params.get('plan');
    const termParam = params.get('billing_cycle');
    if (isBillingTerm(termParam)) {
      setBillingTerm(termParam);
    }
    const allowedPlans = ['starter', 'professional', 'scale'];
    if (planParam && allowedPlans.includes(planParam) && formData.plan !== planParam) {
      setFormData((prev) => ({ ...prev, plan: planParam }));
    }
  }, [location.search]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const submissionPayload = {
        ...formData,
        kvkk_consent: kvkkConsent,
        privacy_consent: privacyConsent,
      };

      // If plan requires payment, redirect to checkout
      if (formData.plan !== 'free' && ['starter', 'professional', 'scale'].includes(formData.plan)) {
        setLoading(false);
        navigate('/onboarding/checkout', { state: { onboardingData: submissionPayload, billingTerm } });
        return;
      }

      // Free plan: create account directly (old flow, only for demo/free)
      const response = await api.post('/api/onboarding/start', submissionPayload);
      
      // Store auth data
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('tenantSlug', response.data.tenant.slug);

      // Navigate to dashboard
      navigate('/app/dashboard');
    } catch (err: any) {
      setError(apiErrorText(err.response?.data?.detail) || t('auth.onboarding.failed'));
      setLoading(false);
    }
  };

  const nextStep = () => {
    // Validate step 1 before moving to step 2
    if (step === 1) {
      if (!formData.email || !formData.password || !formData.password_confirm || !formData.full_name) {
        setError(t('auth.onboarding.fillAllFields'));
        return;
      }
      if (formData.password !== formData.password_confirm) {
        setError(t('auth.onboarding.passwordMismatch'));
        return;
      }
      if (formData.password.length < 6) {
        setError(t('auth.onboarding.passwordMinLength'));
        return;
      }
      if (!emailVerified) {
        setError(t('auth.onboarding.verifyEmailFirst'));
        return;
      }
      if (!kvkkConsent || !privacyConsent) {
        setError(t('auth.onboarding.acceptPolicies'));
        return;
      }
    }
    setError('');
    if (step < 3) setStep(step + 1);
  };

  const sendVerificationCode = async () => {
    if (!formData.email || !formData.full_name) {
      setError(t('auth.onboarding.fillAllFields'));
      return;
    }
    setSendingCode(true);
    setError('');
    try {
      await api.post('/api/verification/send-code', {
        email: formData.email,
        full_name: formData.full_name
      });
      setCodeSent(true);
      setError('');
    } catch (err: any) {
      setError(apiErrorText(err.response?.data?.detail) || t('errors.tryAgain'));
    } finally {
      setSendingCode(false);
    }
  };

  const verifyCode = async () => {
    if (!verificationCode) {
      setError(t('auth.onboarding.fillAllFields'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.post('/api/verification/verify-code', {
        email: formData.email,
        code: verificationCode
      });
      setEmailVerified(true);
      setError('');
    } catch (err: any) {
      setError(apiErrorText(err.response?.data?.detail) || t('errors.tryAgain'));
    } finally {
      setLoading(false);
    }
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-lotus-cream via-white to-lotus-gold/10 flex items-center justify-center p-4 relative">
      <div className="absolute top-6 left-6">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-lotus-gold transition-colors"
        >
          <Home size={18} />
          <span>{t('auth.onboarding.backToHome')}</span>
        </Link>
      </div>
      <div className="max-w-2xl w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-4 hover:opacity-80 transition-opacity">
            <img 
              src="/logo.png" 
              alt="LotusRecruiter" 
              className="w-32 h-32 mx-auto object-contain"
            />
          </Link>
        </div>
        {/* Title */}
        <div className="text-center mb-8 fade-in">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('auth.onboarding.title')}</h1>
          <p className="text-gray-600">{t('auth.onboarding.subtitle')}</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`flex items-center ${s < 3 ? 'flex-1' : ''}`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    step >= s
                      ? 'bg-lotus-gold text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {step > s ? <CheckCircle size={20} /> : s}
                </div>
                {s < 3 && (
                  <div
                    className={`flex-1 h-1 mx-2 ${
                      step > s ? 'bg-lotus-gold' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-gray-600">
            <span>{t('auth.onboarding.step1')}</span>
            <span>{t('auth.onboarding.step2')}</span>
            <span>{t('auth.onboarding.step3')}</span>
          </div>
        </div>

        {/* Form Card */}
        <div className="card fade-in">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Step 1: Account Info */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <User className="text-lotus-gold" size={24} />
                  <h2 className="text-2xl font-bold text-gray-900">{t('auth.onboarding.step1')}</h2>
                </div>

                <div>
                  <label className="label">{t('auth.onboarding.fullName')}</label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    className="input"
                    placeholder={t('auth.onboarding.placeholder.fullName')}
                    required
                  />
                </div>

                <div>
                  <label className="label">{t('auth.onboarding.email')}</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="input"
                    placeholder={t('auth.onboarding.placeholder.email')}
                    required
                    disabled={emailVerified}
                  />
                </div>

                {/* Email Verification */}
                {!emailVerified && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <Mail className="text-blue-600" size={18} />
                      <p className="text-sm font-medium text-blue-900">{t('auth.onboarding.emailVerification')}</p>
                    </div>
                    {!codeSent ? (
                      <button
                        type="button"
                        onClick={sendVerificationCode}
                        disabled={sendingCode || !formData.email || !formData.full_name}
                        className="btn-primary w-full text-sm"
                      >
                        {sendingCode ? t('common.loading') : t('auth.onboarding.sendCode')}
                      </button>
                    ) : (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value)}
                          className="input text-center text-lg tracking-widest font-mono"
                          placeholder={t('auth.onboarding.placeholder.code')}
                          maxLength={6}
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={verifyCode}
                            disabled={loading || !verificationCode}
                            className="btn-primary flex-1 text-sm"
                          >
                            {loading ? t('common.loading') : t('auth.onboarding.verifyCode')}
                          </button>
                          <button
                            type="button"
                            onClick={sendVerificationCode}
                            disabled={sendingCode}
                            className="btn-outline flex-1 text-sm"
                          >
                            {t('auth.onboarding.resendCode')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {emailVerified && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                    <CheckCircle className="text-green-600" size={18} />
                    <p className="text-sm text-green-800 font-medium">{t('auth.onboarding.emailVerified')}</p>
                  </div>
                )}

                <div>
                  <label className="label">{t('auth.onboarding.password')}</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="input with-icon-right"
                      placeholder="••••••••"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{t('validation.minLength', { count: 6 })}</p>
                </div>

                <div>
                  <label className="label">{t('auth.onboarding.confirmPassword')}</label>
                  <div className="relative">
                    <input
                      type={showPasswordConfirm ? "text" : "password"}
                      name="password_confirm"
                      value={formData.password_confirm}
                      onChange={handleChange}
                      className="input with-icon-right"
                      placeholder="••••••••"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPasswordConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* KVKK Consent */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={kvkkConsent}
                      onChange={(e) => setKvkkConsent(e.target.checked)}
                      className="mt-1 w-4 h-4 text-lotus-gold rounded border-gray-300 focus:ring-lotus-gold"
                    />
                    <span className="text-sm text-gray-700 leading-relaxed">
                      <a
                        href="/legal/kvkk-aydinlatma"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-lotus-gold underline hover:text-lotus-gold/80 font-medium"
                      >
                        {t('auth.onboarding.kvkkLink')}
                      </a>{' '}
                      {t('auth.onboarding.kvkkText')}
                    </span>
                  </label>
                </div>

                {/* Privacy Consent */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={privacyConsent}
                      onChange={(e) => setPrivacyConsent(e.target.checked)}
                      className="mt-1 w-4 h-4 text-lotus-gold rounded border-gray-300 focus:ring-lotus-gold"
                    />
                    <span className="text-sm text-gray-700 leading-relaxed">
                      <a
                        href="/legal/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-lotus-gold underline hover:text-lotus-gold/80 font-medium"
                      >
                        {t('auth.onboarding.privacyLink')}
                      </a>{' '}
                      {t('auth.onboarding.privacyText')}
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Step 2: Company Info */}
            {step === 2 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="text-lotus-gold" size={24} />
                  <h2 className="text-2xl font-bold text-gray-900">{t('auth.onboarding.companyDetailsTitle')}</h2>
                </div>

                <div>
                  <label className="label">{t('auth.onboarding.companyName')}</label>
                  <input
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    className="input"
                    placeholder={t('auth.onboarding.placeholder.companyName')}
                    required
                  />
                </div>

                <div>
                  <label className="label">{t('auth.onboarding.industry')}</label>
                  <select
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    className="input"
                  >
                    <option value="">{t('auth.onboarding.selectIndustry')}</option>
                    <option value="Technology">{t('auth.onboarding.industries.technology')}</option>
                    <option value="Finance">{t('auth.onboarding.industries.finance')}</option>
                    <option value="Healthcare">{t('auth.onboarding.industries.healthcare')}</option>
                    <option value="Retail">{t('auth.onboarding.industries.retail')}</option>
                    <option value="Manufacturing">{t('auth.onboarding.industries.manufacturing')}</option>
                    <option value="Other">{t('auth.onboarding.industries.other')}</option>
                  </select>
                </div>

                <div>
                  <label className="label">{t('auth.onboarding.companySize')}</label>
                  <select
                    name="company_size"
                    value={formData.company_size}
                    onChange={handleChange}
                    className="input"
                  >
                    <option value="">{t('auth.onboarding.selectSize')}</option>
                    <option value="1-10">{t('auth.onboarding.companySizes.small')}</option>
                    <option value="11-50">{t('auth.onboarding.companySizes.medium')}</option>
                    <option value="51-200">{t('auth.onboarding.companySizes.large')}</option>
                    <option value="201-500">{t('auth.onboarding.companySizes.enterprise')}</option>
                    <option value="500+">{t('auth.onboarding.companySizes.corporate')}</option>
                  </select>
                </div>

                <div>
                  <label className="label">{t('auth.onboarding.workModel')}</label>
                  <select
                    name="work_model"
                    value={formData.work_model}
                    onChange={handleChange}
                    className="input"
                  >
                    <option value="">{t('auth.onboarding.selectWorkModel')}</option>
                    <option value="Remote">{t('auth.onboarding.workModels.remote')}</option>
                    <option value="Hybrid">{t('auth.onboarding.workModels.hybrid')}</option>
                    <option value="On-site">{t('auth.onboarding.workModels.onsite')}</option>
                  </select>
                </div>

                <div>
                  <label className="label">{t('auth.onboarding.descriptionLabel')}</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="input"
                    rows={3}
                    placeholder={t('auth.onboarding.placeholder.description')}
                  />
                </div>

                <div>
                  <label className="label">{t('auth.onboarding.selectPlan')}</label>
                  <select
                    name="plan"
                    value={formData.plan}
                    onChange={handleChange}
                    className="input"
                    required
                  >
                    <option value="">{t('auth.onboarding.choosePlan')}</option>
                    <option value="starter">{t('auth.onboarding.planStarter')}</option>
                    <option value="professional">{t('auth.onboarding.planProfessional')}</option>
                    <option value="scale">{t('auth.onboarding.planScale')}</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('auth.onboarding.paymentNote')}
                  </p>
                </div>
              </div>
            )}

            {/* Step 3: Confirmation */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle className="text-lotus-gold" size={24} />
                  <h2 className="text-2xl font-bold text-gray-900">{t('auth.onboarding.almostDone')}</h2>
                </div>

                <div className="bg-lotus-cream/50 rounded-lg p-6 space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">{t('auth.onboarding.summaryAccount')}</p>
                    <p className="font-medium">{formData.full_name}</p>
                    <p className="text-sm text-gray-600">{formData.email}</p>
                  </div>
                  <div className="border-t border-gray-200 pt-3">
                    <p className="text-sm text-gray-600">{t('auth.onboarding.summaryCompany')}</p>
                    <p className="font-medium">{formData.company_name}</p>
                    {formData.industry && (
                      <p className="text-sm text-gray-600">{formData.industry}</p>
                    )}
                  </div>
                  <div className="border-t border-gray-200 pt-3">
                    <p className="text-sm text-gray-600">{t('auth.onboarding.summaryPlan')}</p>
                    <p className="font-medium capitalize">{formData.plan || t('auth.onboarding.notSelected')}</p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    {formData.plan && formData.plan !== 'free' 
                      ? t('auth.onboarding.paymentNext')
                      : t('auth.onboarding.accountCreated')}
                  </p>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex gap-3 mt-6">
              {step > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex-1 btn-outline"
                  disabled={loading}
                >
                  {t('common.back')}
                </button>
              )}

              {step < 3 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="flex-1 btn-primary"
                >
                  {t('common.next')}
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 btn-primary"
                >
                  {loading ? t('auth.onboarding.settingUp') : t('auth.onboarding.completeSetup')}
                </button>
              )}
            </div>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600">
            {t('auth.onboarding.haveAccount')}{' '}
            <a href="/login" className="text-lotus-gold hover:text-lotus-gold-dark font-medium">
              {t('auth.onboarding.login')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
